import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

import { supabase } from '../../../lib/supabase';
import { paymentRecoveryService } from '../../../lib/paymentRecoveryService';

const ChurnPreventionWorkflows = ({ churnRiskMembers, onRefresh }) => {
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedCampaign, setSelectedCampaign] = useState(null);

  useEffect(() => {
    loadCampaigns();
  }, []);

  const loadCampaigns = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase?.from('churn_prevention_campaigns')?.select('*')?.order('created_at', { ascending: false });

      setCampaigns(data || []);
    } catch (error) {
      console.error('Error loading campaigns:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLaunchCampaign = async (campaignId) => {
    // Get target members based on campaign risk level
    const campaign = campaigns?.find(c => c?.id === campaignId);
    if (!campaign) return;

    const targetMembers = churnRiskMembers
      ?.filter(m => m?.risk_level === campaign?.target_risk_level)
      ?.map(m => m?.user_id);

    if (targetMembers?.length === 0) {
      alert('No members match the target risk level');
      return;
    }

    if (!confirm(`Launch campaign to ${targetMembers?.length} members?`)) return;

    setLoading(true);
    try {
      const { error } = await paymentRecoveryService?.triggerChurnPreventionCampaign(
        campaignId,
        targetMembers
      );

      if (!error) {
        alert(`Campaign launched to ${targetMembers?.length} members`);
        loadCampaigns();
        onRefresh?.();
      } else {
        alert('Failed to launch campaign');
      }
    } catch (error) {
      alert('Error launching campaign');
    } finally {
      setLoading(false);
    }
  };

  const campaignTypes = [
    {
      type: 'payment_plan',
      name: 'Flexible Payment Plan',
      icon: 'CreditCard',
      description: 'Offer split payments or extended due dates',
      color: 'blue'
    },
    {
      type: 'membership_freeze',
      name: 'Membership Freeze',
      icon: 'Pause',
      description: 'Temporary membership pause option',
      color: 'purple'
    },
    {
      type: 'win_back',
      name: 'Win-Back Offer',
      icon: 'Gift',
      description: 'Special discount or incentive to retain',
      color: 'green'
    },
    {
      type: 'discount_offer',
      name: 'Discount Offer',
      icon: 'Percent',
      description: 'Percentage discount on next payment',
      color: 'orange'
    },
    {
      type: 'personal_outreach',
      name: 'Personal Outreach',
      icon: 'Phone',
      description: 'Direct call from manager or trainer',
      color: 'red'
    }
  ];

  const getCampaignTypeConfig = (type) => {
    return campaignTypes?.find(ct => ct?.type === type) || campaignTypes?.[0];
  };

  const getRiskLevelColor = (level) => {
    const colors = {
      critical: { bg: 'bg-red-100', text: 'text-red-800' },
      high: { bg: 'bg-orange-100', text: 'text-orange-800' },
      medium: { bg: 'bg-yellow-100', text: 'text-yellow-800' },
      low: { bg: 'bg-green-100', text: 'text-green-800' }
    };
    return colors?.[level] || colors?.low;
  };

  return (
    <div className="space-y-6">
      {/* Campaign Types Overview */}
      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Available Campaign Types</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {campaignTypes?.map((type) => (
            <div key={type?.type} className="border border-gray-200 rounded-lg p-4 bg-white">
              <div className="flex items-start gap-3">
                <div className={`w-10 h-10 rounded-lg bg-${type?.color}-50 flex items-center justify-center`}>
                  <Icon name={type?.icon} className={`w-5 h-5 text-${type?.color}-600`} />
                </div>
                <div className="flex-1">
                  <h4 className="font-semibold text-gray-900 mb-1">{type?.name}</h4>
                  <p className="text-sm text-gray-600">{type?.description}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Active Campaigns */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">Active Campaigns</h3>
          <Button
            iconName="Plus"
            onClick={() => setShowCreateModal(true)}
          >
            Create Campaign
          </Button>
        </div>

        {loading ? (
          <div className="text-center py-8">
            <Icon name="Loader2" className="w-8 h-8 animate-spin text-primary mx-auto" />
          </div>
        ) : campaigns?.length === 0 ? (
          <div className="text-center py-12 bg-gray-50 rounded-lg">
            <Icon name="Inbox" className="w-12 h-12 text-gray-400 mx-auto mb-3" />
            <p className="text-gray-600 mb-4">No campaigns created yet</p>
            <Button onClick={() => setShowCreateModal(true)}>Create Your First Campaign</Button>
          </div>
        ) : (
          <div className="space-y-4">
            {campaigns?.map((campaign) => {
              const typeConfig = getCampaignTypeConfig(campaign?.campaign_type);
              const riskColor = getRiskLevelColor(campaign?.target_risk_level);
              const conversionRate = campaign?.total_sent > 0 
                ? ((campaign?.total_conversions / campaign?.total_sent) * 100)?.toFixed(1)
                : 0;

              return (
                <div key={campaign?.id} className="border border-gray-200 rounded-lg p-4 bg-white">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-3">
                        <div className={`w-10 h-10 rounded-lg bg-${typeConfig?.color}-50 flex items-center justify-center`}>
                          <Icon name={typeConfig?.icon} className={`w-5 h-5 text-${typeConfig?.color}-600`} />
                        </div>
                        <div>
                          <h4 className="font-semibold text-gray-900">{campaign?.campaign_name}</h4>
                          <p className="text-sm text-gray-600">{typeConfig?.name}</p>
                        </div>
                        <span className={`px-2 py-1 rounded text-xs font-medium ${riskColor?.bg} ${riskColor?.text}`}>
                          Target: {campaign?.target_risk_level?.toUpperCase()}
                        </span>
                        {campaign?.is_active && (
                          <span className="px-2 py-1 rounded text-xs font-medium bg-green-100 text-green-800">
                            ACTIVE
                          </span>
                        )}
                      </div>

                      <div className="bg-gray-50 rounded-lg p-3 mb-3">
                        <p className="text-sm text-gray-700">{campaign?.message_template}</p>
                      </div>

                      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 text-sm">
                        <div>
                          <p className="text-gray-600">Total Sent</p>
                          <p className="font-semibold text-gray-900">{campaign?.total_sent}</p>
                        </div>
                        <div>
                          <p className="text-gray-600">Responses</p>
                          <p className="font-semibold text-gray-900">{campaign?.total_responses}</p>
                        </div>
                        <div>
                          <p className="text-gray-600">Conversions</p>
                          <p className="font-semibold text-gray-900">{campaign?.total_conversions}</p>
                        </div>
                        <div>
                          <p className="text-gray-600">Success Rate</p>
                          <p className="font-semibold text-gray-900">{campaign?.success_rate?.toFixed(1)}%</p>
                        </div>
                        <div>
                          <p className="text-gray-600">Conversion Rate</p>
                          <p className="font-semibold text-gray-900">{conversionRate}%</p>
                        </div>
                      </div>

                      {campaign?.offer_details && (
                        <div className="mt-3 p-3 bg-blue-50 rounded-lg">
                          <p className="text-sm font-medium text-blue-900 mb-1">Offer Details:</p>
                          <div className="text-sm text-blue-800">
                            {Object.entries(campaign?.offer_details || {})?.map(([key, value]) => (
                              <p key={key}>
                                <strong>{key?.replace('_', ' ')}:</strong> {JSON.stringify(value)}
                              </p>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>

                    <div className="flex flex-col gap-2 ml-4">
                      <Button
                        size="sm"
                        iconName="Send"
                        onClick={() => handleLaunchCampaign(campaign?.id)}
                        disabled={!campaign?.is_active || loading}
                      >
                        Launch Campaign
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        iconName="BarChart3"
                      >
                        View Analytics
                      </Button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* A/B Testing Info */}
      <div className="bg-green-50 rounded-lg border border-green-200 p-4">
        <h4 className="font-semibold text-green-900 mb-2 flex items-center gap-2">
          <Icon name="TrendingUp" className="w-5 h-5" />
          Automated A/B Testing
        </h4>
        <p className="text-sm text-green-800">
          All churn prevention campaigns automatically track response rates, conversion metrics, and member engagement. 
          The system optimizes message templates and offer combinations based on performance data to maximize retention effectiveness.
        </p>
      </div>
    </div>
  );
};

export default ChurnPreventionWorkflows;